import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryParams } from '@/@diffrag/hooks/useQueryParams'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { IAdminUserRO, StatusEnum3 } from 'api'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
const STATUS_OPTIONS = [
  {
    label: '전체',
    value: undefined,
  },
  {
    label: '활성',
    value: StatusEnum3.ACTIVE,
  },
  {
    label: '제휴종료',
    value: StatusEnum3.INACTIVE,
  },
]

export default function Clinic() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    undefined,
  )
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    undefined,
  )
  const navigate = useNavigate()
  const { data: categoryList } = useApi({
    id: 'categoryList',
    fn: api().adminCategory.adminCategoryList,
  })

  const { query } = useQueryParams<{
    status?: StatusEnum3
    categoryId?: number
  }>({
    status: undefined,
    categoryId: undefined,
  })

  const { data, isFetching, refetch } = useApi({
    id: 'clinicList',
    fn: api().adminClinic.adminClinicList,
    params: {
      page,
      categoryIds: selectedCategory
        ? [selectedCategory]
        : query.categoryId
          ? [query.categoryId]
          : [],
      regionIds: [],
      status: selectedStatus ? (selectedStatus as StatusEnum3) : query.status,
      q,
    },
  })

  const selectedCategoryLabels = useMemo(() => {
    if (!selectedCategory) {
      return '전체'
    }
    return (
      categoryList?.items?.find((item) => item.id === selectedCategory)?.name ||
      '전체'
    )
  }, [selectedCategory, categoryList])

  const selectedStatusLabels = useMemo(() => {
    if (!selectedStatus) {
      return '전체'
    }
    return (
      STATUS_OPTIONS.find((option) => option.value === selectedStatus)?.label ||
      '전체'
    )
  }, [selectedStatus])

  const handleCategorySelect = (categoryId: number | undefined) => {
    setSelectedCategory(categoryId)
  }

  const handleStatusSelect = (status: string | undefined) => {
    setSelectedStatus(status)
  }

  const onDelete = async (rows: IAdminUserRO[]) => {
    await Promise.all(
      rows.map((row) => api().adminClinic.inactiveCreate(row.id)),
    )
    refetch()
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">카테고리 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedCategoryLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              <DropdownMenuCheckboxItem
                checked={selectedCategory === undefined}
                onCheckedChange={() => handleCategorySelect(undefined)}
              >
                전체
              </DropdownMenuCheckboxItem>
              {categoryList?.items?.map((category) => (
                <DropdownMenuCheckboxItem
                  key={category.id}
                  checked={selectedCategory === category.id}
                  onCheckedChange={() => handleCategorySelect(category.id)}
                >
                  {category.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">상태 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedStatusLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              {STATUS_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedStatus === option.value}
                  onCheckedChange={() => handleStatusSelect(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SimpleTable
        options={{
          disableSort: true,
        }}
        totalCount={data?.total}
        createHref="/clinics/edit"
        onDelete={onDelete}
        onRowClick={(row) => {
          navigate(`/clinics/edit?id=${row.clinic.id}`)
        }}
        deleteText="제휴종료"
        columns={[
          {
            label: 'NO',
            key: 'id',
            width: 100,
            isPrimary: true,
          },
          {
            label: '카테고리',
            key: 'clinic.category.name',
            isPrimary: true,
          },
          {
            label: '병원명',
            key: 'clinic.name',
            isPrimary: true,
          },
          {
            label: '아이디',
            key: 'email',
          },
          {
            label: '담당자',
            key: 'clinic.name',
          },
          {
            label: '연락처',
            key: 'clinic.phone',
          },
          {
            label: '상태',
            key: 'clinic.status',
            valueMap: {
              ACTIVE: '활성',
              INACTIVE: '제휴종료',
            },
          },
        ]}
        data={data?.users}
        isLoading={isFetching}
      />
      <OffsetPagination page={page} total={data?.total} pageSize={20} />
    </ContentView>
  )
}
